import { PrimerImportDialog } from "@components/primers/PrimerImportDialog";
import { StrandBadge } from "@components/StrandBadge";
import type { GnomuxPrimer } from "@db/content";
import { useLibraryPrimers } from "@db/primers";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@ui/empty";
import { Input } from "@ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@ui/table";
import { FlaskConicalIcon, ImportIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { PrimerDetailsSheet } from "../components/primers/PrimerDetailsSheet";

export const Route = createFileRoute("/library/primers")({
  component: PrimersComponent,
});

type DirectionFilter = "all" | "forward" | "reverse";

function PrimersComponent() {
  const primers = useLibraryPrimers();
  const [search, setSearch] = useState("");
  const [directionFilter, setDirectionFilter] =
    useState<DirectionFilter>("all");
  const [selectedPrimer, setSelectedPrimer] = useState<GnomuxPrimer | null>(
    null,
  );
  const [importOpen, setImportOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!primers) return [];

    return primers.filter((primer) => {
      const matchesSearch =
        primer.name.toLowerCase().includes(search.toLowerCase()) ||
        primer.sequence.toLowerCase().includes(search.toLowerCase());

      const matchesDirection =
        directionFilter === "all" || primer.direction === directionFilter;

      return matchesSearch && matchesDirection;
    });
  }, [primers, search, directionFilter]);

  function truncateSequence(sequence: string, maxLength = 30) {
    if (sequence.length <= maxLength) return sequence;
    return `${sequence.slice(0, maxLength)}...`;
  }

  if (!primers) {
    return null;
  }

  return (
    <>
      {primers.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-4">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FlaskConicalIcon />
              </EmptyMedia>
              <EmptyTitle>No primers saved</EmptyTitle>
              <EmptyDescription>
                Design primers from the sequence viewer or import from a file.
              </EmptyDescription>
            </EmptyHeader>
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <ImportIcon />
              Import
            </Button>
          </Empty>
        </div>
      ) : (
        <div className="p-4">
          <div className="mb-4 flex gap-2">
            <Input
              placeholder="Search primers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
            />
            <Select
              value={directionFilter}
              onValueChange={(value) =>
                setDirectionFilter(value as DirectionFilter)
              }
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="forward">Forward</SelectItem>
                <SelectItem value="reverse">Reverse</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <ImportIcon />
              Import
            </Button>
          </div>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-48">Name</TableHead>
                <TableHead className="w-64">Sequence</TableHead>
                <TableHead className="w-24">Direction</TableHead>
                <TableHead className="w-20">Tm</TableHead>
                <TableHead className="w-20">GC%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((primer) => (
                <TableRow
                  key={primer.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedPrimer(primer)}
                >
                  <TableCell className="font-medium">{primer.name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {truncateSequence(primer.sequence)}
                  </TableCell>
                  <TableCell>
                    <StrandBadge strand={primer.direction} />
                  </TableCell>
                  <TableCell>{primer.tm.toFixed(1)}°C</TableCell>
                  <TableCell>{primer.gcPercent.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PrimerDetailsSheet
            primer={selectedPrimer}
            onClose={() => setSelectedPrimer(null)}
          />
        </div>
      )}
      <PrimerImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </>
  );
}
